<template>
  <div>
    <b-row class="justify-content-end">
      <b-col lg="2">
        <b-button variant="success" class="float-right my-1" @click="openAddTaskModal"><feather-icon
            icon="PlusIcon" />Add Task</b-button>
      </b-col>
    </b-row>

    <div class="d-flex justify-content-center" v-if="loading">
      <b-spinner variant="success" label="Spinning" style="width: 3rem; height: 3rem"></b-spinner>
    </div>
    <b-col cols="12" class="d-flex justify-content-end mb-2" style="position: relative; z-index: 1">
      <b-button variant="outline-danger" size="sm" @click="clearFilters">
        Clear Filters
      </b-button>
    </b-col>

    <b-row>
      <b-table :items="filteredTasks" :fields="taskFields" striped hover responsive :per-page="perPage"
        :current-page="currentPage">
        <!-- Title Column with Filter -->
        <template #head(title)="scope">
          <div class="d-flex flex-column">
            <span>Title</span>
            <b-form-input v-model="filters.title" placeholder="Filter by Title" class="mt-1" size="sm"></b-form-input>
          </div>
        </template>
        <template #cell(title)="data">
          <h4>{{ data.item.title ? data.item.title : "-N/A-" }}</h4>
        </template>

        <!-- Sales Person Column with Filter -->
        <template #head(salesPerson)="scope">
          <div class="d-flex flex-column">
            <span>Sales Person</span>
            <b-form-input v-model="filters.salesPerson" placeholder="Filter by Sales Person" class="mt-1"
              size="sm"></b-form-input>
          </div>
        </template>
        <template #cell(salesPerson)="data">
          <h5>
            {{
              getSalePersonName(data.item.salesPersonId)
                ? getSalePersonName(data.item.salesPersonId)
                : "-N/A-"
            }}
          </h5>
        </template>

        <template #head(dueDate)="scope">
          <div class="d-flex flex-column">
            <span>Due Date</span>
            <DatePicker v-model="filters.dueDateRange" range format="YYYY-MM-DD" placeholder="Select date range"
              @change="applyDateFilters" style="width: 100%" />
          </div>
        </template>
        <template #cell(dueDate)="data">
          <h6>
            {{ data.item.dueDate ? formatDate(data.item.dueDate) : "-N/A-" }}
          </h6>
        </template>

        <template #head(status)="data">
          <div class="d-flex flex-column">
            <span>Status</span>
            <b-form-select v-model="filters.status" :options="statusFilterOptions" class="mt-1" size="sm"></b-form-select>
          </div>
        </template>

        <template #cell(status)="data">
          <h6>{{ data.item.status }}</h6>
        </template>

        <!-- Tag Column with Dropdown Filter -->
        <template #head(tag)="scope">
          <div class="d-flex flex-column">
            <span>Tag</span>
            <b-form-select v-model="filters.tag" :options="tagFilterOptions" class="mt-1" size="sm"></b-form-select>
          </div>
        </template>

        <template #cell(tag)="data">
          <b-badge variant="bg-transparent" class="d-flex">
            <feather-icon icon="CircleIcon" :class="[
              'mr-1 text-' + getImportanceVariant(data.item.isImportant),
            ]" size="20" />
            <h5>{{ data.item.isImportant ? "Important" : "Mild" }}</h5>
          </b-badge>
        </template>

        <!-- Description Column with Filter -->
        <template #head(description)="scope">
          <div class="d-flex flex-column">
            <span>Description</span>
            <b-form-input v-model="filters.description" placeholder="Filter by Description" class="mt-1"
              size="sm"></b-form-input>
          </div>
        </template>
        <template #cell(description)="data">
          <p>{{ data.item.description ? data.item.description : "-N/A-" }}</p>
        </template>

        <template #cell(action)="data">
          <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
            <template v-slot:button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="text-body align-middle mr-25" />
            </template>
            <b-dropdown-item @click="openEditModal(data.item)">
              <feather-icon icon="EditIcon" class="mr-50" />
              <span>View / Edit</span>
            </b-dropdown-item>
            <b-dropdown-item @click="confirmDelete(data.item)">
              <feather-icon icon="Trash2Icon" class="mr-50" />
              <span>Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-row>
    <b-row class="justify-content-center">
      <b-pagination v-model="currentPage" :total-rows="filteredTasks.length" :per-page="perPage"
        aria-controls="taskTable" class="mt-1"></b-pagination>
    </b-row>
    <!--- Add Task Modal -->
    <b-modal id="addTaskModal" title="Add Task" v-model="showAddTaskModal" centered @ok="handleAddTask" size="lg">
      <b-row>
        <b-col md="6">
          <b-form-group label="Task Title" label-for="TaskTitleInput" :state="titleValidation"
            :invalid-feedback="titleError">
            <b-form-input id="TaskTitleInput" required v-model="title" @change="validateTitle"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Tag" label-for="tagInput">
            <v-select :options="optionTags" label="title" v-model="selectedTag" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Sales Person" label-for="salesPersonInput" :state="salespersonValidation"
            :invalid-feedback="salespersonError">
            <b-form-select id="salesPersonId" v-model="salesPersonId" :options="optionsSalesPerson" required
              @input="validateSalesperson" />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Due Date" label-for="dueDateInput" :state="dueDateValidation"
            :invalid-feedback="dueDateError">
            <b-form-datepicker id="example-purchase" class="mb" v-model="dueDate" required @input="validateDueDate" />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Status" label-for="status">
            <b-form-select :options="optionStatus" v-model="selectedStatus" />
          </b-form-group>
        </b-col>

        <b-col md="12">
          <b-form-group label="Description" label-for="editDescription">
            <b-form-textarea id="editDescription" v-model="description"></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>
      <template #modal-footer>
        <b-button @click="handleAddTask">
          <b-spinner v-if="loadingAdd" type="grow" small></b-spinner>
          <span v-if="!loading">Add Task</span>
        </b-button>
      </template>
    </b-modal>
    <!-- View Task Modal -->
    <b-modal id="viewModal" title="View Task" v-model="showViewModal" centered>
      <p><strong>Task Title: </strong>{{ selectedTask.title }}</p>

      <p>
        <strong>Sales Person: </strong>{{ getSalePersonName(selectedTask.salesPersonId) }}
      </p>
      <p><strong>Description: </strong>{{ selectedTask.description }}</p>
      <p><strong>Date: </strong>{{ formatDate(selectedTask.dueDate) }}</p>
      <p>
        <strong>Status: </strong><b-badge variant="bg-transparent" class="d-flex">
          <feather-icon icon="CircleIcon" :class="[
            'mr-1 text-' + getImportanceVariant(selectedTask.isImportant),
          ]" size="20" />
          <h5>{{ selectedTask.isImportant ? "Important" : "Mild" }}</h5>
        </b-badge>
      </p>
      <template #modal-footer></template>
    </b-modal>
    <!-- Edit Task Modal -->
    <b-modal id="editTaskModal" title="Add Task" v-model="showEditTaskModal" centered size="lg">
      <b-row>
        <b-col md="6">
          <b-form-group label="Task Title" label-for="TaskTitleInput" :state="editTitleValidation"
            :invalid-feedback="editTitleError">
            <b-form-input id="TaskTitleInput" v-model="selectedTask.title" required
              @change="validateEditTitle"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Tag" label-for="tagInput">
            <v-select :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="optionTags" label="title"
              v-model="selectedTag" :clearable="false" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Sales Person" label-for="salesPersonInput">
            <b-form-select v-model="selectedTask.salesPersonId" :options="optionsSalesPerson" />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Due Date" label-for="dueDateInput" :state="editDueDateValidation"
            :invalid-feedback="editDueDateError">
            <b-form-datepicker id="example-purchase" class="mb-1" v-model="selectedTask.dueDate" required
              @blur="validateEditDueDate"></b-form-datepicker>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Status" label-for="status">
            <b-form-select :options="optionStatus" v-model="selectedTask.status" />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group label="Description" label-for="editDescription">
            <b-form-textarea id="editDescription" v-model="selectedTask.description"></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>
      <template #modal-footer>
        <b-button @click="handleEditTask">
          <b-spinner v-if="loading" type="grow" small></b-spinner>
          <span v-if="!loading">Update Task</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import {
  BPagination,
  BListGroup,
  BListGroupItem,
  BFormDatepicker,
  BCard,
  BRow,
  BCol,
  BButton,
  BCardTitle,
  BCardSubTitle,
  BBadge,
  BDropdown,
  BSpinner,
  BDropdownItem,
  BModal,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BInputGroup,
  BInputGroupAppend,
  BForm,
  BFormSelect,
  BTable,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import { fnSortingDropdown } from "@/libs/customFn";

import { mapActions, mapGetters } from "vuex";
export default {
  components: {
    BPagination,
    BListGroup,
    BListGroupItem,
    BFormDatepicker,
    BSpinner,
    BCard,
    BRow,
    BCol,
    BButton,
    BCardTitle,
    BCardSubTitle,
    BBadge,
    BDropdown,
    BDropdownItem,
    BModal,
    BFormGroup,
    BFormInput,
    vSelect,
    flatPickr,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    BForm,
    BFormSelect,
    BTable,
    DatePicker,
  },
  props: {
    heading: {
      type: String,
      default: "To Do",
    },
  },
  data() {
    return {
      titleValidation: null,
      descriptionValidation: null,
      dueDateValidation: null,
      titleError: "",
      descriptionError: "",
      dueDateError: "",
      salespersonError: "",
      currentPage: 1,
      perPage: 5,
      pageOptions: [3, 5, 10],
      filterOn: [],
      optionsSalesPerson: [],
      totalRows: 1,
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      salespersonValidation: null,
      editTitleValidation: null,
      editDescriptionValidation: null,
      editDueDateValidation: null,
      editTitleError: "",
      editDescriptionError: "",
      editDueDateError: "",
      id: this.$route.params.id,
      loadingAdd: false,
      title: "",
      description: "",
      selectedStatus: "",
      selectedTag: "",
      salesPersonId: "",
      showAddTaskModal: false,
      showViewModal: false,
      showEditTaskModal: false,
      selectedTask: { status: "NEW" },
      dueDate: null,
      isImportant: false,
      filters: {
        title: "",
        salesPerson: "",
        dueDateRange: [null, null],
        tag: null,
        status: null,
        description: "",
      },
      taskFields: [
        { key: "title", label: "Title", sortable: true },
        { key: "salesPerson", label: "Sales Person", sortable: true },
        { key: "dueDate", label: "Due Date", sortable: true },
        { key: "tag", label: "Tag", sortable: true },
        { key: "status", label: "Status", sortable: true },

        { key: "description", label: "Description", sortable: true },
        { key: "action", label: "Actions" },
      ],
      taskItems: [
        {
          title: "Title 1",
          tag: "Mild",
          salesPerson: "Person 1",
          dueDate: "02.10.2024",
          description: "Description 1",
        },
      ],
      tagFilterOptions: [
        { text: "All", value: null },
        { text: "Mild", value: "Mild" },
        { text: "Important", value: "Important" },
      ],
      statusFilterOptions: [
        { value: null, text: "ALL" },,
        { value: "NEW", text: "NEW" },
        { value: "INPROGRESS", text: "INPROGRESS" },
        { value: "COMPLETED", text: "COMPLETED" },
      ],

      optionTags: [
        { title: "Mild", value: false },
        { title: "Important", value: true },
      ],

      optionStatus: [
        { value: "NEW", text: "NEW" },
        { value: "INPROGRESS", text: "INPROGRESS" },
        { value: "COMPLETED", text: "COMPLETED" },
      ],
    };
  },

  computed: {
    filteredTasks() {
      return this.sortedTasks.filter((task) => {
        const matchesTitle = this.filters.title
          ? task.title?.toLowerCase().includes(this.filters.title.toLowerCase())
          : true;

        const matchesSalesPerson = this.filters.salesPerson
          ? this.getSalePersonName(task.salesPersonId)
            ?.toLowerCase()
            .includes(this.filters.salesPerson.toLowerCase())
          : true;

        const matchesTag = this.filters.tag
          ? (task.isImportant ? "Important" : "Mild") === this.filters.tag
          : true;

        const matchesDueDate =
          this.filters.dueDateRange[0] && this.filters.dueDateRange[1]
            ? new Date(task.dueDate) >= new Date(this.filters.dueDateRange[0]) &&
            new Date(task.dueDate) <= new Date(this.filters.dueDateRange[1])
            : true;

        const matchesDescription = this.filters.description
          ? task.description
            ?.toLowerCase()
            .includes(this.filters.description.toLowerCase())
          : true;

        // Add status filter logic here
        const matchesStatus = this.filters.status
          ? task.status === this.filters.status
          : true;

        return (
          matchesTitle &&
          matchesSalesPerson &&
          matchesTag &&
          matchesDueDate &&
          matchesDescription &&
          matchesStatus
        );
      });
    },
    ...mapGetters("salesPersonModule", {
      SalesPersonList: "salesperson",
      loadingSales: "loading",
    }),

    ...mapGetters("taskModule", {
      sortedTasks: "taskbyleadid",
      loading: "loading",
    }),
    // optionsSalesPerson() {
    //   return this.SalesPersonList.map((v) => ({
    //     value: v.id,
    //     text: `${v.firstName} ${v.lastName}`,
    //   }));
    // },

    // sortedTasks() {
    //   if(this.Tasks == null || this.Tasks.length == 0) return this.Tasks;
    //   return this.Tasks.sort((a, b) => {
    //     const dateA = new Date(a.created_On);
    //     const dateB = new Date(b.created_On);
    //     return dateB - dateA;
    //   });
    // },
  },
  methods: {
    ...mapActions("salesPersonModule", ["getSalesPersonListAction"]),
    ...mapActions("taskModule", [
      "getTaskListAction",
      "getTaskByLeadIdAction",
      "updateTaskAction",
      "removeTaskAction",
      "addTaskAction",
    ]),

    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    formatDate(isoString) {
      const date = new Date(isoString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-indexed in JavaScript
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
    handleAddTask() {

      if (!this.loadingAdd) {
      this.addTasks();
      }
    },
    handleEditTask() {
      this.validateEditTitle();
      this.validateEditDueDate();

      if (this.editTitleValidation && this.editDueDateValidation) {
        this.editTask();
      }
    },

    validateEditTitle() {
      if (!this.selectedTask.title) {
        this.editTitleValidation = false;
        this.editTitleError = "Title is required";
      } else {
        this.editTitleValidation = true;
        this.editTitleError = "";
      }
    },

    validateEditDescription() {
      if (!this.selectedTask.description) {
        this.editDescriptionValidation = false;
        this.editDescriptionError = "Description is required";
      } else {
        this.editDescriptionValidation = true;
        this.editDescriptionError = "";
      }
    },

    validateEditDueDate() {
      if (!this.selectedTask.dueDate) {
        this.editDueDateValidation = false;
        this.editDueDateError = "Due date is required";
      } else {
        this.editDueDateValidation = true;
        this.editDueDateError = "";
      }
    },
    validateTitle() {
      if (!this.title) {
        this.titleValidation = false;
        this.titleError = "Title is required";
      } else {
        this.titleValidation = true;
        this.titleError = "";
      }
    },
    validateSalesperson() {
      if (!this.salesPersonId) {
        this.salespersonValidation = false;
        this.salespersonError = "Salesperson is required";
      } else {
        this.salespersonValidation = true;
        this.salespersonError = "";
      }
    },
    validateDescription() {
      if (!this.description) {
        this.descriptionValidation = false;
        this.descriptionError = "Description is required";
      } else {
        this.descriptionValidation = true;
        this.descriptionError = "";
      }
    },
    validateDueDate() {
      if (!this.dueDate) {
        this.dueDateValidation = false;
        this.dueDateError = "Due date is required";
      } else {
        this.dueDateValidation = true;
        this.dueDateError = "";
      }
    },
    editTask() {
      const payload = {
        id: this.selectedTask.id,
        title: this.selectedTask.title,
        salesPersonId: this.selectedTask.salesPersonId,
        dueDate: this.selectedTask.dueDate,
        tag: this.selectedTag.value ? "Important" : "Mild",
        status: this.selectedTask.status,
        description: this.selectedTask.description,
        isImportant: this.selectedTag.value,
        isActive: this.selectedTask.isActive,
        leadClientId: this.id,
      };

      this.updateTaskAction(payload)
        .then((response) => {
          //this.getTasks(this.id);
          this.$swal({
            icon: "success",
            title: "Success!",
            text: "Task updated successfully.",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
          this.showEditTaskModal = false;
        })
        .catch((error) => {
          this.$swal({
            icon: "error",
            title: "Error!",
            text: "Failed to update Task.",
            customClass: {
              confirmButton: "btn btn-warning",
            },
          });
        });
    },

    getImportanceVariant(isImportant) {
      return isImportant ? "danger" : "info";
    },

    getSalePersonName(id) {
      const salesPerson = this.SalesPersonList.find((s) => s.id === id);
      return salesPerson
        ? salesPerson.firstName + " " + salesPerson.lastName
        : "";
    },

    //  getTasks(id) {
    //   //this.loading = true;
    //   this.getTaskByLeadIdAction(id);

    //     // .then((response) => {
    //     //   this.Tasks = response.data.result;
    //     //   console.log("tasksssssssssssssssssssssss", this.Tasks);
    //     //   this.loading = false;
    //     // })
    //     // .catch((error) => {
    //     //   this.loading = false;
    //     // });
    // },

    async addTasks() {
      this.loadingAdd = true;
      this.validateTitle();
      this.validateDueDate();
      this.validateSalesperson();
      if (
        this.titleValidation &&
        this.dueDateValidation &&
        this.salespersonValidation
      ) {
        const isImportantValue = this.selectedTag?.value === true;

        let payload = {
          title: this.title,
          salesPersonId: this.salesPersonId,
          dueDate: this.dueDate,
          tag: isImportantValue ? "Important" : "Mild",
          status: this.selectedStatus,
          description: this.description,
          isImportant: this.selectedTag.value,
          isActive: true,
          leadClientId: this.id,
        };

        await this.addTaskAction(payload)
          .then((response) => {
            // this.getTasks(this.id);
            this.$swal({
              icon: "success",
              title: "Success!",
              text: "Task added successfully.",
              customClass: {
                confirmButton: "btn btn-success",
              },
            });

            this.title = "";
            this.salesPersonId = "";
            this.dueDate = null;
            this.description = "";
            this.selectedTag = "";
            this.isImportant = false;
            this.showAddTaskModal = false;
          })
          .catch((error) => {
            this.loadingAdd = false;
            this.$swal({
              icon: "error",
              title: "Error!",
              text: "Failed to add Task.",
              customClass: {
                confirmButton: "btn btn-success",
              },
            });
          });

        await this.getTaskByLeadIdAction(this.id);
        loadingAdd = false;
      }
    },

    openAddTaskModal() {
      this.showAddTaskModal = true;
    },
    openViewModal(Task) {
      this.selectedTask = Task;
      this.showViewModal = true;
    },
    openEditModal(Task) {
      this.selectedTask = Task;
      this.selectedTag = this.optionTags.find(
        (tag) => tag.value === Task.isImportant
      );
      this.showEditTaskModal = true;
    },
    getTypeVariant() {
      return this.isImportant ? "danger" : "info";
    },

    getTagVariant(tag) {
      switch (tag) {
        case "Important":
          return "danger";
        case "Mild":
          return "info";
        default:
          return "secondary";
      }
    },

    async deleteTasks(taskId) {
      this.loading = true;
      await this.removeTaskAction(taskId)
        .then(async (response) => {
          // this.getTasks(this.id);
          this.loading = false;
          this.$swal({
            icon: "success",
            title: "Deleted!",
            text: "The Task has been deleted.",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });

          await this.getTaskByLeadIdAction(this.id);
        })
        .catch((error) => {
          //this.getTasks(this.id);
          this.loading = false;
          this.$swal({
            icon: "error",
            title: "Error",
            text: "The Task has been deleted.",
            customClass: {
              confirmButton: "btn btn-warning",
            },
          });
        });
    },

    // deleting a Task
    confirmDelete(Task) {
      this.$swal({
        title: "Delete Task?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteTasks(Task.id);
        }
      });
    },
    clearFilters() {
      // Reset filters to a new object to trigger reactivity
      this.filters = {
        title: "",
        salesPerson: "",
        dueDateRange: [null, null],
        tag: null,
        description: "",
      };
      this.currentPage = 1; // Optionally reset pagination to the first page
    },
    applyDateFilters() {
      this.currentPage = 1; // Reset pagination if applicable
    },
  },

  async mounted() {
    await this.getTaskByLeadIdAction(this.id);
    await this.getSalesPersonListAction().then(() => {
      this.SalesPersonList.map((v) =>
        this.optionsSalesPerson.push({
          value: v.id,
          text: `${v.firstName}` + " " + `${v.lastName}`,
        })
      );
    });
    this.optionsSalesPerson = fnSortingDropdown(this.optionsSalesPerson);
  },
};
</script>
<style lang="scss" scoped>
.is-done {
  color: #666;
}
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
